import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Title } from "../../../../themes/Default/components/Title";
import { Main } from "../../../../themes/Default/components/Main";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../../../themes/Default/components/List";
import { TableHead } from "../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import { Slide, toast, ToastContainer } from "react-toastify";
import { Header as ListHeader } from "../../../../themes/Default/components/List/components/Header";
import { Wizard } from "../../../../themes/Default/components/Wizard";
import { Step } from "../../../../themes/Default/components/Wizard/Step";
import { Body as ListBody } from "../../../../themes/Default/components/List/components/Body";
import Checkbox from "../../../../themes/Default/components/Form/components/Checkbox/Checkbox";
import { useUserToken } from "../../../../hooks/core/UserToken";
import { roles } from "../../../../services/utils/Roles";

const ReportsStudent = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const { groupId } = useParams();
    const { reportId } = useParams();
    const { useListAttendancesMutation, useViewGroupMutation, useDownloadReportMutation, useViewReportStudentMutation, useVerifyReportMutation, useSendReportMutation} = groupsManagement;
    const [viewReportStudent] = useViewReportStudentMutation();
    const [attendancesList, { isLoading }] = useListAttendancesMutation();
    const [students, setStudents] = useState();
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [disableButton, setDisableButton] = useState(true);

    const [groupName, setGroupName] = useState("");
    const [coordinatorUserId, setCoordinatorUserId] = useState(0);
    const [viewGroup] = useViewGroupMutation();
    const [downloadReport] = useDownloadReportMutation();
    const [verifyReport] = useVerifyReportMutation();
    const [sendReport] = useSendReportMutation();

    const [studentReports, setStudentReports] = useState({});

    const [hasTests, setHasTests] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);

    const tabs = [
        { title: t('groups.attendance'),onClick: () => gotoAttendanceTab(groupId)  },
        ...(hasPlan ? [
            { title: t('groups.planning'), onClick: () => gotoPlanningTab(groupId)},
        ] : []),
        ...(hasProgress ? [
            { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
        ] : []),
        ...(hasTests ? [
            { title: t('groups.progressTest'), onClick: () => gotoProgressTestsTab(groupId) }
        ] : []),
        { title: t('reports.title'),isActive: true},
        { title: t('documents'), onClick: () => gotoDocumentsTab(groupId)},
        { title: t('notes'), onClick: () => gotoNotesTab(groupId)},
    ];

    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    if (
        decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
        decodedToken?.roles.includes(roles.SECRETARY) ||
        decodedToken?.roles.includes(roles.DIRECTOR) || 
        decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
    ) {
        tabs.push({
            title: t("settings.title"),
            onClick: () => gotoEditTab(groupId),
        });
    }

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    }

    const gotoProgressTestsTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    }

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    }

    const gotoEditTab = (groupId) => {
        navigate("/groups/edit/" + groupId);
    }

    const gotoEditReport = () => {
        navigate("/groups/" + groupId + "/reports/edit/" + reportId);
    }

    const gotoGeneralGrade = (studentId) => {
        navigate("/groups/" + groupId + "/student/" + studentId + "/reports/" + reportId + "/general-grade");
    }

    const onVerify = () => {
        for (let i = 0; i < selectedStudents?.length; i++) {
            const studentId = selectedStudents[i];
            verifyReport({reportId, studentId }).then((result) => {
                if (result?.error) {
                    toast.error(result.error.data.error.responseMessage);
                } else {
                    toast.info(result?.data?.info?.responseMessage);
                    loadReports();
                }
            });
        }        
    };

    const onSend = () => {
        for (let i = 0; i < selectedStudents?.length; i++) {
            const studentId = selectedStudents[i];
            sendReport({reportId, studentId}).then((result) => {
                if (result?.error) {
                    toast.error(result.error.data.error.responseMessage);
                } else {
                    toast.info(result?.data?.info?.responseMessage);
                    loadReports();
                }
            });
        }  
    };

    const previewReport = () => {
        for (let i = 0; i < selectedStudents?.length; i++) {
            const studentId = selectedStudents[i];
            downloadReport({ reportId, studentId }).then((response) => {
                const blob = response.data;
                if (blob instanceof Blob) {
                    const url = window.URL.createObjectURL(blob);
                    window.open(url);
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", `student_report_${studentId}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    
                    window.URL.revokeObjectURL(url);
                }
                loadReports();
            });
        } 
    };

    const onSelectionChange = (id) => {
        setSelectedStudents((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const loadReports = () => {
        if (students?.length > 0) {
            students.forEach((student) => {
                viewReportStudent({ reportId, studentId: student.id }).then((response) => {
                    setStudentReports((prev) => ({
                        ...prev,
                        [student.id]: response?.data
                    }));
                });
            });
        }
    };

    useEffect(() => {
        if (selectedStudents.length > 0) {
            setDisableButton(false);
        } else {
            setDisableButton(true);
        }
    }, [selectedStudents]);

    useEffect(() => {
        attendancesList({ groupId }).then((result) => {
            setStudents(result?.data?.students);
        });
        viewGroup(groupId).then((result) => {
            setGroupName(result?.data?.name);
            setHasTests(result?.data?.hasTests);
            setHasProgress(result?.data?.hasProgress);
            setHasPlan(result?.data?.hasPlan);
            setCoordinatorUserId(result?.data?.coordinatorUserId);
        });
    }, []);

    useEffect(() => {
        loadReports()
    }, [students]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={"/groups/" + groupId + "/reports/edit/" + reportId} />
                <Button title={t("preview")} isDisabled={disableButton} onClick={() => previewReport()} icon="fa-regular fa-eye"/>
                {(decodedToken?.roles.includes(roles.SUPER_ADMIN) || decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES) || decodedToken?.roles.includes(roles.SECRETARY) || decodedToken?.id === coordinatorUserId) && (
                    <>
                        <Button title={t("verify")} isDisabled={disableButton} onClick={() => onVerify()} icon="fa-regular fa-check"/>
                        <Button title={t("send")} isDisabled={disableButton} onClick={() => onSend()} icon="fa-regular fa-paper-plane"/>
                    </>
                )} 
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{groupName}</Title>
                <Breadcrumps>
                    <Link title={t("groups.title")} icon="fa-user-group" />
                    <Link title={t("reports.title")} isActive={true} />
                </Breadcrumps>
                <br/>
                <br/>
                <br/>
                <Wizard>
                    <Step title={t("reports.type")} isChecked={true} onClick={gotoEditReport} />
                    <Step title={t("reports.individualReports")}  isActive={true} />
                </Wizard>
                <br/>
                <br/>
                <br/>
                <List>
                    <ListHeader>                
                    </ListHeader>
                    <ListBody>
                        <TableHead>
                            <TableRow>
                                <TableHeadCell></TableHeadCell>
                                <TableHeadCell></TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("Status u grupi")}</span>
                                </TableHeadCell>
                                <TableHeadCell>
                                    <span>{t("groups.fullName")}</span>
                                </TableHeadCell>
                                <TableHeadCell>{t("reports.verified")}</TableHeadCell>
                                <TableHeadCell>{t("reports.sent")}</TableHeadCell>
                                <TableHeadCell></TableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {students?.length > 0 &&
                                students.map((student, index) => {
                                    return (
                                        <TableRow key={student.id}>
                                            <TableDataCell>
                                                {studentReports[student.id]?.isVerified || student.isArchived 
                                                    ? null 
                                                    : (
                                                        <Button 
                                                            icon="fa-regular fa-pen" 
                                                            onClick={() => gotoGeneralGrade(student.id)} 
                                                            color={studentReports[student.id]?.hasGeneralGrade ? "#27ae60" : undefined}
                                                        />
                                                    )
                                                }
                                            </TableDataCell>
                                            <TableDataCell>
                                                {!student.isArchived && (
                                                   <Checkbox 
                                                        name="isBlockClass" 
                                                        value={student.id} 
                                                        onChange={() => onSelectionChange(student.id)} 
                                                    />
                                                )}
                                            </TableDataCell>
                                            <TableDataCell>
                                                <span style={{ color: student.isArchived ? '#ff0000' : '#27ae60' }}>
                                                    {student.isArchived ? t("Arhiviran") : t("Aktivan")}
                                                </span>
                                            </TableDataCell>
                                            <TableDataCell>{student.name}</TableDataCell>
                                            <TableDataCell>
                                                <span style={{ color: studentReports[student.id]?.isVerified ? '#27ae60' : '#ff0000' }}>
                                                    {studentReports[student.id]?.isVerified ? t("groups.hasStartedYes") : t("groups.hasStartedNo")}
                                                </span>
                                            </TableDataCell>
                                            <TableDataCell>
                                                <span style={{ color: studentReports[student.id]?.hasSent ? '#27ae60' : '#ff0000' }}>
                                                    {studentReports[student.id]?.hasSent ? t("groups.hasStartedYes") : t("groups.hasStartedNo")}
                                                </span>
                                            </TableDataCell>    
                                            <TableDataCell></TableDataCell>                                   
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </ListBody>
                </List>
            </Main>
        </DefaultLayout>
    );
};

export default ReportsStudent;
